import React, { createContext, useContext, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import postHogService from '../services/PostHogService'
import { useAuth } from './AuthContext'

/**
 * PostHog Context
 *
 * Provides analytics tracking throughout the application.
 * - Initializes PostHog on mount
 * - Automatically tracks route changes
 * - Identifies users on authentication
 * - Resets tracking on logout
 */

const PostHogContext = createContext({})

export const usePostHog = () => {
  return useContext(PostHogContext)
}

export const PostHogProvider = ({ children }) => {
  const location = useLocation()
  const { user } = useAuth()

  // Initialize PostHog on mount
  useEffect(() => {
    postHogService.init()
  }, [])

  // Identify user for analytics (does not track sign-in event - that should be tracked in AuthContext)
  useEffect(() => {
    if (postHogService.enabled && user) {
      // Identify user with relevant properties
      postHogService.identify(user.id, {
        email: user.email,
        user_id: user.id,
        // Add any other relevant user metadata
        created_at: user.created_at
      })
    } else if (postHogService.enabled && !user) {
      // Reset tracking on logout
      postHogService.reset()
    }
  }, [user])

  // Track page context on route changes using super properties
  // Note: Basic pageview tracking is handled automatically by PostHog
  // with defaults: '2026-01-30' (capture_pageview: 'history_change')
  // This effect registers/unregisters super properties for accurate per-page analytics
  useEffect(() => {
    if (!postHogService.enabled) return

    // Parse the current route to extract meaningful context
    const path = location.pathname
    const properties = {}

    // Extract page context from URL
    if (path.includes('/project/')) {
      // Parse project routes
      const parts = path.split('/')
      const projectIdx = parts.indexOf('project')
      const targetIdx = parts.indexOf('target')

      if (projectIdx !== -1 && projectIdx + 1 < parts.length) {
        properties.project_id = parts[projectIdx + 1]
      }

      if (targetIdx !== -1 && targetIdx + 1 < parts.length) {
        properties.target_name = parts[targetIdx + 1]
      }

      // Determine specific page type
      if (path.includes('/settings')) {
        properties.page_type = 'project_settings'
      } else if (path.includes('/symbols')) {
        properties.page_type = 'symbols_view'
      } else if (path.includes('/compare')) {
        properties.page_type = 'build_compare'
      } else if (path.includes('/region-config')) {
        properties.page_type = 'region_config'
      } else if (targetIdx !== -1) {
        properties.page_type = 'dashboard'
      }

      // Register super properties for project-scoped pages
      postHogService.registerSuperProperties(properties)
    } else {
      // Non-project pages - unregister project context and set page type only
      postHogService.unregisterSuperProperties(['project_id', 'target_name'])

      // Set page type for non-project pages
      if (path === '/' || path === '') {
        properties.page_type = 'projects_list'
      } else if (path === '/pricing') {
        properties.page_type = 'pricing'
      } else if (path === '/auth') {
        properties.page_type = 'authentication'
      }

      if (properties.page_type) {
        postHogService.registerSuperProperties(properties)
      }
    }
  }, [location])

  // Tracking helper functions for components to use
  const trackEvent = useCallback((eventName, properties = {}) => {
    postHogService.trackEvent(eventName, properties)
  }, [])

  const trackPageView = useCallback((pageName, properties = {}) => {
    postHogService.trackPageView(pageName, properties)
  }, [])

  const setUserProperties = useCallback((properties = {}) => {
    postHogService.setPersonProperties(properties)
  }, [])

  const value = {
    trackEvent,
    trackPageView,
    setUserProperties,
    enabled: postHogService.enabled
  }

  return (
    <PostHogContext.Provider value={value}>
      {children}
    </PostHogContext.Provider>
  )
}
